<template>
  <div class="mb-4 max-w-2xl rounded-lg bg-purple-200/50 p-3">
    <!-- Initial Suggestion State -->
    <div
      v-if="currentState === 'suggestion'"
      class=""
    >
      <div class="mb-3 flex items-start justify-between">
        <div class="flex items-center gap-2">
          <Lightbulb class="h-4 w-4 text-purple-600" />
          <span class="text-sm text-purple-600">
            {{ t('wouldYouLikeToSaveAsPreference') }}
          </span>
        </div>
        <button
          class="text-purple-400 hover:text-purple-600"
          @click="dismissSuggestion"
        >
          <X class="h-4 w-4" />
        </button>
      </div>

      <div class="rounded-lg bg-white p-4">
        <h3 class="mb-2 text-sm font-semibold text-gray-900">{{ suggestion.title }}</h3>
        <p class="mb-4 text-sm text-gray-600">{{ suggestion.content }}</p>

        <div class="flex items-center justify-between">
          <div class="w-md">
            <SelectField
              v-model="editForm.scope"
              name="scope"
              :options="scopeOptions"
              :disabled="false"
              class="compact-select"
            />
          </div>
          <div class="flex items-center gap-2">
            <button
              class="flex items-center gap-1 rounded px-3 py-1.5 text-sm text-gray-600 transition-colors
                hover:bg-gray-100"
              @click="editSuggestion"
            >
              <Edit class="h-4 w-4" />
              {{ t('edit') }}
            </button>
            <button
              :disabled="isSaving"
              class="w-full rounded bg-purple-600 px-4 py-1.5 text-sm text-white transition-colors hover:bg-purple-700
                disabled:opacity-50"
              @click="saveSuggestion"
            >
              <span v-if="isSaving">{{ t('saving') }}...</span>
              <span v-else>{{ t('save') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit State -->
    <div
      v-else-if="currentState === 'editing'"
      class="suggestion-card"
    >
      <div class="mb-3 flex items-start justify-between">
        <div class="flex items-center gap-2">
          <Lightbulb class="h-4 w-4 text-purple-600" />
          <span class="text-sm text-purple-600">
            {{ t('wouldYouLikeToSaveAsPreference') }}
          </span>
        </div>
        <button
          class="text-gray-400 hover:text-gray-600"
          @click="dismissSuggestion"
        >
          <X class="h-4 w-4" />
        </button>
      </div>

      <div class="rounded-lg bg-white p-4">
        <div class="mb-4 space-y-3">
          <div>
            <div class="mb-2 flex items-center justify-between">
              <label class="text-sm font-medium text-gray-700">{{ t('title') }}</label>
              <button
                type="button"
                class="inline-flex items-center gap-1 rounded-md bg-purple-100 px-2 py-1 text-xs font-medium
                  text-purple-700 hover:bg-purple-200 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="isGeneratingTitle || !editForm.content"
                @click="generateTitle"
              >
                <span
                  v-if="isGeneratingTitle"
                  class="animate-spin"
                >
                  <LoaderCircle class="h-3 w-3" />
                </span>
                <span v-else>
                  <RotateCw class="h-3 w-3" />
                </span>
                {{ t('generateWithAi') }}
              </button>
            </div>
            <input
              v-model="editForm.title"
              type="text"
              class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent
                focus:outline-none focus:ring-2 focus:ring-purple-500"
              :placeholder="t('enterTitle')"
            />
            <div class="mt-1 text-right text-xs text-gray-500">
              {{ editForm.title.length }}/50 {{ t('characters') }}
            </div>
          </div>

          <div>
            <label class="mb-2 block text-sm font-medium text-gray-700">{{ t('prompt') }}</label>
            <textarea
              v-model="editForm.content"
              rows="4"
              class="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-transparent
                focus:outline-none focus:ring-2 focus:ring-purple-500"
              :placeholder="t('enterPrompt')"
            ></textarea>
          </div>
        </div>

        <div class="flex items-center justify-between">
          <div class="w-md">
            <SelectField
              v-model="editForm.scope"
              name="scope"
              :options="scopeOptions"
              :disabled="false"
              class="compact-select"
            />
          </div>
          <div class="flex items-center gap-2">
            <button
              class="flex items-center gap-1 rounded px-3 py-1.5 text-sm text-gray-600 transition-colors
                hover:bg-gray-100"
              @click="revertEdits"
            >
              <X class="h-4 w-4" />
              {{ t('revertEdits') }}
            </button>
            <button
              :disabled="isSaving || !isFormValid"
              class="rounded bg-purple-600 px-4 py-1.5 text-sm text-white transition-colors hover:bg-purple-700
                disabled:opacity-50"
              @click="saveSuggestion"
            >
              <span v-if="isSaving">{{ t('saving') }}...</span>
              <span v-else>{{ t('save') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Saved State -->
    <div
      v-else-if="currentState === 'saved'"
      class="flex items-center justify-between rounded-lg bg-purple-100 px-4 py-1.5"
    >
      <div class="flex items-center gap-2">
        <CheckCircle class="h-5 w-5 text-purple-600" />
        <span class="text-sm font-medium text-purple-600">{{ t('preferenceSaved') }}</span>
      </div>

      <button
        class="flex items-center gap-1 text-sm font-medium text-purple-600 underline transition-colors
          hover:text-purple-700"
        @click="viewInLibrary"
      >
        {{ t('viewInPreferenceLibrary') }}
        <ArrowRight class="h-4 w-4" />
      </button>
    </div>

    <!-- Dismissed State -->
    <div
      v-else-if="currentState === 'dismissed'"
      class="suggestion-card opacity-60"
    >
      <div class="flex items-center gap-2">
        <X class="h-4 w-4 text-gray-500" />
        <span class="text-xs text-gray-500">{{ t('preferenceSuggestionDiscarded') }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import SelectField from '@/components/admin/form/SelectField.vue'
  import { useToast } from '@/components/ui/toast/use-toast'
  import { useTranslation } from '@/composables/useTranslation'
  import {
    ArrowRight,
    CheckCircle,
    Edit,
    Lightbulb,
    LoaderCircle,
    RotateCw,
    User,
    Users,
    X
  } from 'lucide-vue-next'
  import { computed, onMounted, ref } from 'vue'

  import { ChatPreferencesService } from '@/types/generated/services.gen'

  onMounted(() => {
    console.log('PreferenceSuggestion mounted')
  })

  const { t } = useTranslation({
    en: {
      preferenceSuggestion: 'Preference suggestion',
      wouldYouLikeToSaveAsPreference:
        'Would you like to save this as a preference for future chats?',
      personalPreference: 'Personal preference',
      sharedPreference: 'Shared preference',
      edit: 'Edit',
      save: 'Save',
      saving: 'Saving',
      title: 'Title',
      generateWithAi: 'Generate with AI',
      enterTitle: 'Enter title',
      characters: 'characters',
      prompt: 'Prompt',
      enterPrompt: 'Enter prompt',
      revertEdits: 'Revert edits',
      preferenceSaved: 'Preference saved',
      viewInPreferenceLibrary: 'View in preference library',
      preferenceSuggestionDiscarded: 'Preference suggestion discarded',
      titleGenerationError: 'Error generating title',
      preferenceSavedDescription: 'Your preference has been saved successfully',
      errorSavingPreference: 'Error saving preference'
    },
    de: {
      preferenceSuggestion: 'Präferenz-Vorschlag',
      wouldYouLikeToSaveAsPreference:
        'Möchten Sie dies als Präferenz für zukünftige Chats speichern?',
      personalPreference: 'Persönliche Präferenz',
      sharedPreference: 'Gemeinsame Präferenz',
      edit: 'Bearbeiten',
      save: 'Speichern',
      saving: 'Speichern',
      title: 'Titel',
      generateWithAi: 'Mit KI generieren',
      enterTitle: 'Titel eingeben',
      characters: 'Zeichen',
      prompt: 'Prompt',
      enterPrompt: 'Prompt eingeben',
      revertEdits: 'Änderungen rückgängig',
      preferenceSaved: 'Präferenz gespeichert',
      viewInPreferenceLibrary: 'In Präferenz-Bibliothek anzeigen',
      preferenceSuggestionDiscarded: 'Präferenz-Vorschlag verworfen',
      titleGenerationError: 'Fehler beim Generieren des Titels',
      preferenceSavedDescription: 'Ihre Präferenz wurde erfolgreich gespeichert',
      errorSavingPreference: 'Fehler beim Speichern der Präferenz'
    }
  })
  const { toast } = useToast()

  // Scope options for the SelectField
  const scopeOptions = computed(() => [
    {
      value: 'USER',
      title: t('personalPreference'),
      icon: User
    },
    {
      value: 'ORGANIZATION',
      title: t('sharedPreference'),
      icon: Users
    }
  ])

  interface PreferenceSuggestion {
    id: string
    title: string
    content: string
    reasoning: string
    suggested_scope: string
    state?: string
    created_preference_uuid?: string | null
  }

  interface Props {
    suggestion: PreferenceSuggestion
  }

  const props = defineProps<Props>()
  const emit = defineEmits<{
    dismissed: []
    saved: [preference: any]
    viewInLibrary: []
  }>()

  // State management - initialize based on suggestion state
  const getInitialState = () => {
    const suggestionState = props.suggestion.state || 'PENDING'
    switch (suggestionState) {
      case 'ACCEPTED':
        return 'saved'
      case 'DISMISSED':
        return 'dismissed'
      default:
        return 'suggestion'
    }
  }

  const currentState = ref<'suggestion' | 'editing' | 'saved' | 'dismissed'>(getInitialState())
  const isSaving = ref(false)
  const isGeneratingTitle = ref(false)
  const savedPreference = ref<any>(null)

  // Edit form
  const editForm = ref({
    title: props.suggestion.title,
    content: props.suggestion.content,
    scope: props.suggestion.suggested_scope || 'USER'
  })

  // Form validation
  const isFormValid = computed(() => {
    return (
      editForm.value.title.trim().length > 0 &&
      editForm.value.content.trim().length > 0 &&
      editForm.value.title.length <= 50
    )
  })

  // Actions
  const editSuggestion = () => {
    currentState.value = 'editing'
  }

  const revertEdits = () => {
    editForm.value = {
      title: props.suggestion.title,
      content: props.suggestion.content,
      scope: props.suggestion.suggested_scope || 'USER'
    }
    currentState.value = 'suggestion'
  }

  const generateTitle = async () => {
    if (!editForm.value.content || isGeneratingTitle.value) return

    isGeneratingTitle.value = true
    editForm.value.title = ''

    try {
      const response = await ChatPreferencesService.chatPreferencesGenerateTitleCreate({
        requestBody: {
          content: editForm.value.content
        }
      })

      // Simulate streaming effect
      const title = response.title
      let currentIndex = 0

      const streamInterval = setInterval(() => {
        if (currentIndex < title.length) {
          editForm.value.title += title[currentIndex]
          currentIndex++
        } else {
          clearInterval(streamInterval)
          isGeneratingTitle.value = false
        }
      }, 15)
    } catch (error) {
      console.error('Error generating title:', error)
      toast({
        title: t('titleGenerationError'),
        variant: 'destructive'
      })
      isGeneratingTitle.value = false
    }
  }

  const saveSuggestion = async () => {
    if (!isFormValid.value) return

    isSaving.value = true

    try {
      const response = await ChatPreferencesService.chatPreferencesSaveSuggestionCreate({
        requestBody: {
          title: editForm.value.title,
          content: editForm.value.content,
          scope: editForm.value.scope,
          source: 'AI_CHAT',
          suggestion_id: props.suggestion.id
        }
      })

      savedPreference.value = response
      currentState.value = 'saved'

      toast({
        title: t('preferenceSaved'),
        description: t('preferenceSavedDescription')
      })

      emit('saved', response)
    } catch (error) {
      console.error('Error saving preference:', error)
      toast({
        title: t('errorSavingPreference'),
        variant: 'destructive'
      })
    } finally {
      isSaving.value = false
    }
  }

  const dismissSuggestion = async () => {
    try {
      await ChatPreferencesService.chatPreferencesDismissSuggestionCreate({
        requestBody: {
          suggestion_id: props.suggestion.id
        }
      })

      currentState.value = 'dismissed'
      emit('dismissed')
    } catch (error) {
      console.error('Error dismissing suggestion:', error)
      toast({
        title: 'Error dismissing suggestion',
        variant: 'destructive'
      })
    }
  }

  const viewInLibrary = () => {
    emit('viewInLibrary')
  }
</script>

<style scoped></style>
